import { Directive, inject, Input, TemplateRef } from '@angular/core';

export interface TableV2CellContext<T> {
  $implicit: T;
  index: number;
}

export interface TableV2RowAfterContext<T> extends TableV2CellContext<T> {
  colspan: number;
}

/**
 * Content of the cells of a column: `<ng-template appTableV2Cell="price" [appTableV2CellOf]="rows" let-row>`.
 * `appTableV2CellOf` only types `let-row`. Without a template the cell shows `column.value(row)` or `row[column.key]`.
 */
@Directive({
  selector: '[appTableV2Cell]',
  standalone: false,
})
export class TableV2CellDirective<T> {
  @Input({ alias: 'appTableV2Cell', required: true }) key = '';
  @Input() appTableV2CellOf?: readonly T[];
  readonly template = inject(TemplateRef);

  static ngTemplateContextGuard<T>(
    _directive: TableV2CellDirective<T>,
    context: unknown,
  ): context is TableV2CellContext<T> {
    return !!context;
  }
}

/** Custom header of a column: `<ng-template appTableV2Header="price">` */
@Directive({
  selector: '[appTableV2Header]',
  standalone: false,
})
export class TableV2HeaderDirective {
  @Input({ alias: 'appTableV2Header', required: true }) key = '';
  readonly template = inject(TemplateRef);
}

/**
 * Rows of its own after every row: nested rows, an inline panel. The template renders `<tr>`s,
 * the context gives the row, its index and `colspan` to span the whole table.
 */
@Directive({
  selector: '[appTableV2RowAfter]',
  standalone: false,
})
export class TableV2RowAfterDirective<T> {
  /** Only types `let-row` */
  @Input() appTableV2RowAfterOf?: readonly T[];
  readonly template = inject(TemplateRef);

  static ngTemplateContextGuard<T>(
    _directive: TableV2RowAfterDirective<T>,
    context: unknown,
  ): context is TableV2RowAfterContext<T> {
    return !!context;
  }
}

/** Replaces the empty state, e.g. to add buttons */
@Directive({
  selector: '[appTableV2Empty]',
  standalone: false,
})
export class TableV2EmptyDirective {
  readonly template = inject(TemplateRef);
}
